import React, { lazy } from "react";
import AuthLayout from "../layout/AuthLayout";

const LoginPage = lazy(() => import("../modules/auth/pages/LoginPage"));
const SignUpPage = lazy(() => import("../modules/auth/pages/SignUpPage"));
const StaffAccessOnboardingPage = lazy(() => import("../modules/auth/pages/StaffAccessOnboardingPage"));
const LandingPage = lazy(() => import("../modules/public/LandingPage"));
const FeaturesPage = lazy(() => import("../modules/public/FeaturesPage"));
const PricingPage = lazy(() => import("../modules/public/PricingPage"));
const AboutPage = lazy(() => import("../modules/public/AboutPage"));
const ContactPage = lazy(() => import("../modules/public/ContactPage"));
const CreateHospital = lazy(() => import("../modules/hospital/CreateHospital"));
const TermsPage = lazy(() => import("../modules/public/TermsPage"));
const PrivacyPage = lazy(() => import("../modules/public/PrivacyPage"));
const Dashboard = lazy(() => import("../modules/dashboard/Dashboard"));
const AdminDashboard = lazy(() => import("../modules/dashboard/pages/AdminDashboard"));
const DoctorDashboard = lazy(() => import("../modules/dashboard/pages/DoctorDashboard"));
const PetOwnerDashboard = lazy(() => import("../modules/dashboard/pages/PetOwnerDashboard"));
const PharmacistDashboard = lazy(() => import("../modules/dashboard/pages/PharmacistDashboard"));
const DispenseQueue = lazy(() => import("../modules/pharmacy/DispenseQueue"));
const DispenseDetail = lazy(() => import("../modules/pharmacy/DispenseDetail"));
const ReceptionCheckIn = lazy(() => import("../modules/reception/ReceptionCheckIn"));
const AppointmentWorkspace = lazy(() => import("../modules/appointments/pages/AppointmentWorkspace"));
const ApprovalsDashboard = lazy(() => import("../modules/approvals/pages/ApprovalsDashboard"));
const ApprovalAnalyticsPage = lazy(() => import("../modules/approvals/pages/ApprovalAnalyticsPage"));
const FinanceDashboard = lazy(() => import("../modules/finance/FinanceDashboard"));
const FinanceInvoiceList = lazy(() => import("../modules/finance/invoices/InvoiceList"));
const FinanceInvoiceDetail = lazy(() => import("../modules/finance/invoices/InvoiceDetail"));
const FinancePaymentList = lazy(() => import("../modules/finance/payments/PaymentList"));
const FinanceReportsPage = lazy(() => import("../modules/finance/reports/ReportsPage"));
const SubscriptionBillingPage = lazy(() => import("../modules/subscription/BillingPage"));
const ClinicalWorkspace = lazy(() => import("../modules/clinical/pages/ClinicalWorkspace"));
const DoctorList = lazy(() => import("../modules/doctors/pages/DoctorList"));
const CreateDoctor = lazy(() => import("../modules/doctors/pages/CreateDoctor"));
const CustomersList = lazy(() => import("../modules/customers/pages/CustomersList"));
const LabWorkspace = lazy(() => import("../modules/labs/pages/LabWorkspace"));
const NotificationsWorkspace = lazy(() => import("../modules/notifications/pages/NotificationsWorkspace"));
const PetsList = lazy(() => import("../modules/pets/pages/PetsList"));
const PetHistory = lazy(() => import("../modules/pets/pages/PetHistory"));
const InventoryList = lazy(() => import("../modules/inventory/pages/InventoryList"));
const ReportsWorkspace = lazy(() => import("../modules/reports/pages/ReportsWorkspace"));
const ResourcesWorkspace = lazy(() => import("../modules/resources/pages/ResourcesWorkspace"));
const AuditLogsPage = lazy(() => import("../modules/resources/pages/AuditLogsPage"));
const PermissionManagementPage = lazy(() => import("../modules/resources/pages/PermissionManagementPage"));
const ProceduresWorkspace = lazy(() => import("../modules/resources/pages/ProceduresWorkspace"));
const SchedulingWorkspace = lazy(() => import("../modules/resources/pages/SchedulingWorkspace"));
const FinanceOperationsWorkspace = lazy(() => import("../modules/resources/pages/FinanceOperationsWorkspace"));
const VaccinationWorkspace = lazy(() => import("../modules/vaccinations/pages/VaccinationWorkspace"));
const SuperAdminDashboard = lazy(() => import("../modules/super-admin/pages/SuperAdminDashboard"));
const TenantList = lazy(() => import("../modules/super-admin/pages/TenantList"));
const TenantDetails = lazy(() => import("../modules/super-admin/pages/TenantDetails"));
const SubscriptionOverview = lazy(() => import("../modules/super-admin/pages/SubscriptionOverview"));
const PlatformAnalytics = lazy(() => import("../modules/super-admin/pages/PlatformAnalytics"));

const platformRoles = ["super_admin", "platform_staff"];
const frontDeskRoles = ["hospital_admin", "branch_admin", "staff", "receptionist"];
const clinicalRoles = ["super_admin", "hospital_admin", "branch_admin", "doctor"];
const allAuthenticatedRoles = ["super_admin", "hospital_admin", "branch_admin", "staff", "receptionist", "pharmacist", "doctor", "pet_owner"];
const appointmentRoles = ["super_admin", "hospital_admin", "branch_admin", "staff", "receptionist", "doctor", "pet_owner"];
const pilotAdminRoles = ["super_admin", "hospital_admin", "branch_admin"];
const proceduresRoles = ["super_admin", "hospital_admin", "branch_admin", "staff", "doctor"];
const schedulingRoles = ["super_admin", "hospital_admin", "branch_admin", "staff", "receptionist", "doctor"];
const financeOpsRoles = ["super_admin", "hospital_admin", "branch_admin", "staff", "receptionist"];
const approvalRoles = ["super_admin", "hospital_admin", "branch_admin", "pharmacist"];
const financeUserRoles = ["hospital_admin", "branch_admin", "staff", "receptionist", "doctor"];

export const publicRoutes = [
  { path: "/", element: <LandingPage /> },
  { path: "/features", element: <FeaturesPage /> },
  { path: "/pricing", element: <PricingPage /> },
  { path: "/about", element: <AboutPage /> },
  { path: "/contact", element: <ContactPage /> },
  { path: "/contact-sales", element: <ContactPage /> },
  { path: "/terms", element: <TermsPage /> },
  { path: "/privacy", element: <PrivacyPage /> },
  { path: "/signin", element: <AuthLayout><LoginPage /></AuthLayout> },
  { path: "/login", element: <AuthLayout><LoginPage /></AuthLayout> },
  { path: "/register", element: <AuthLayout><SignUpPage /></AuthLayout> },
  { path: "/signup", element: <AuthLayout><SignUpPage /></AuthLayout> },
  { path: "/staff-access/:token", element: <StaffAccessOnboardingPage /> }
];

export const protectedRoutes = [
  { path: "/admin", element: <SuperAdminDashboard />, roles: platformRoles, permissions: ["analytics.view", "tenants.view"] },
  { path: "/admin/tenants", element: <TenantList />, roles: platformRoles, permissions: ["tenants.view"] },
  { path: "/admin/tenants/:id", element: <TenantDetails />, roles: platformRoles, permissions: ["tenants.detail"] },
  { path: "/admin/subscriptions", element: <SubscriptionOverview />, roles: platformRoles, permissions: ["subscriptions.view"] },
  { path: "/admin/analytics", element: <PlatformAnalytics />, roles: platformRoles, permissions: ["analytics.view"] },
  { path: "/create-hospital", element: <CreateHospital />, roles: allAuthenticatedRoles },
  { path: "/dashboard", element: <Dashboard />, roles: frontDeskRoles },
  { path: "/reception", element: <ReceptionCheckIn />, roles: frontDeskRoles },
  { path: "/admin-dashboard", element: <AdminDashboard />, roles: ["super_admin", "hospital_admin"] },
  { path: "/doctor-dashboard", element: <DoctorDashboard />, roles: ["doctor"] },
  { path: "/pet-owner-dashboard", element: <PetOwnerDashboard />, roles: ["pet_owner"] },
  { path: "/pharmacist-dashboard", element: <PharmacistDashboard />, roles: ["pharmacist"] },
  { path: "/pharmacy/queue", element: <DispenseQueue />, roles: ["receptionist", "staff", "pharmacist"] },
  { path: "/pharmacy/queue/:id", element: <DispenseDetail />, roles: ["receptionist", "staff", "pharmacist"] },
  { path: "/appointments", element: <AppointmentWorkspace />, roles: appointmentRoles },
  { path: "/approvals", element: <ApprovalsDashboard />, roles: approvalRoles },
  { path: "/approval-analytics", element: <ApprovalAnalyticsPage />, roles: approvalRoles },
  { path: "/finance", element: <FinanceDashboard />, roles: financeUserRoles },
  { path: "/subscription", element: <SubscriptionBillingPage />, roles: ["super_admin", "hospital_admin"] },
  { path: "/invoices", element: <FinanceInvoiceList />, roles: ["staff", "receptionist", "doctor"] },
  { path: "/invoices/:id", element: <FinanceInvoiceDetail />, roles: ["staff", "receptionist", "doctor"] },
  { path: "/payments", element: <FinancePaymentList />, roles: ["staff", "receptionist", "doctor"] },
  { path: "/payments/new", element: <FinancePaymentList />, roles: ["staff", "receptionist", "doctor"] },
  { path: "/finance/reports", element: <FinanceReportsPage />, roles: ["hospital_admin", "branch_admin"] },
  { path: "/clinical", element: <ClinicalWorkspace />, roles: clinicalRoles },
  { path: "/doctors", element: <DoctorList />, roles: ["super_admin", "hospital_admin"] },
  { path: "/doctors/new", element: <CreateDoctor />, roles: ["super_admin", "hospital_admin"] },
  { path: "/labs", element: <LabWorkspace />, roles: clinicalRoles },
  { path: "/vaccinations", element: <VaccinationWorkspace />, roles: clinicalRoles },
  { path: "/reports", element: <ReportsWorkspace />, roles: ["super_admin", "hospital_admin", "branch_admin", "staff", "doctor"] },
  {
    path: "/scheduling",
    element: <SchedulingWorkspace />,
    roles: schedulingRoles
  },
  {
    path: "/procedures",
    element: <ProceduresWorkspace />,
    roles: proceduresRoles
  },
  {
    path: "/inventory-ops",
    element: (
      <ResourcesWorkspace
        title="Inventory Operations"
        subtitle="Review purchase orders, batches, stock movement history, suppliers, and bundles."
        defaultResource="purchase-orders"
        resourceKeys={["purchase-orders", "inventory-batches", "inventory-movements", "suppliers", "product-bundles", "product-categories"]}
      />
    ),
    roles: pilotAdminRoles
  },
  {
    path: "/finance-ops",
    element: <FinanceOperationsWorkspace />,
    roles: financeOpsRoles
  },
  { path: "/resources", element: <ResourcesWorkspace />, roles: pilotAdminRoles },
  { path: "/settings/permissions", element: <PermissionManagementPage />, roles: ["super_admin", "hospital_admin", "branch_admin"], permissions: ["security.read"] },
  { path: "/audit-logs", element: <AuditLogsPage />, roles: ["super_admin", "hospital_admin", "branch_admin"] },
  {
    path: "/settings",
    element: (
      <ResourcesWorkspace
        title="Hospital Setup"
        subtitle="Manage branch setup, staff roles, master data, and operational defaults used across the hospital."
      />
    ),
    roles: ["super_admin", "hospital_admin", "branch_admin"]
  },
  { path: "/notifications", element: <NotificationsWorkspace />, roles: allAuthenticatedRoles },
  { path: "/pets", element: <PetsList />, roles: allAuthenticatedRoles },
  { path: "/pets/:petId/history", element: <PetHistory />, roles: allAuthenticatedRoles },
  { path: "/customers", element: <CustomersList />, roles: ["super_admin", "hospital_admin", "branch_admin", "staff", "receptionist", "doctor"] },
  { path: "/inventory", element: <InventoryList />, roles: ["super_admin", "hospital_admin", "branch_admin", "pharmacist"] }
];
